import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, trackByValue } from '@y42/angular/util';
import { IconModule } from '@y42/core/icon';
import { StopPropagationModule } from '@y42/core/stop-propagation';
import { isNotNullish } from '@y42/util/nullish';
import { IRowNode } from 'ag-grid-community';
import { map } from 'rxjs';
import { BaseCellRendererComponent, Params } from '../base-cell-renderer.component';

export interface MenuOption {
  label: string;
  action: () => void;
  class?: string;
  permissionAllowed?: boolean;
  permissionTooltip?: string;
}

export interface MenuCellRendererParams<T> {
  triggerIconClass?: (node: IRowNode<T>) => string;
  options: (node: IRowNode<T>) => MenuOption[];
}

type MenuCellRendererValue = unknown;
type MenuCellRendererData = unknown;
type ParamsType = Params<unknown, MenuCellRendererData, MenuCellRendererParams<MenuCellRendererData>>;

@UntilDestroy()
@Component({
  selector: 'y42-menu-cell-renderer',
  templateUrl: './menu-cell-renderer.component.html',
  styleUrls: ['./menu-cell-renderer.component.scss'],
  standalone: true,
  imports: [CommonModule, IconModule, MatMenuModule, MatButtonModule, StopPropagationModule, MatTooltipModule],
})
export class MenuCellRendererComponent extends BaseCellRendererComponent<
  MenuCellRendererValue,
  MenuCellRendererData,
  ParamsType
> {
  readonly trackByValue = trackByValue;

  readonly state = connectState(this, {
    params: this.params$,
    options: this.params$.pipe(
      map((params) =>
        params.options(params.node).map((option) => ({
          ...option,
          permissionAllowed: isNotNullish(option.permissionAllowed) ? option.permissionAllowed : true,
          permissionTooltip: option.permissionTooltip || '',
        })),
      ),
    ),
    triggerIconClass: this.params$.pipe(map((params) => params.triggerIconClass?.(params.node) || '')),
  });
}
